import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Menu, X, Headphones, Wrench, Book, Database, Smartphone, ShoppingCart, Briefcase, BarChart, Info, Network, Layers, Lightbulb, Package } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { Link, useLocation } from 'react-router-dom';
import { useAppContext } from '@/contexts/AppContext';
import { useTranslation } from '@/hooks/useTranslation';
import { ROUTES } from '@/constants';
import { NAVIGATION_CONFIG, NavigationItem, NavigationGroup } from '@/constants/navigation';
import { getSeoRoute } from '@/lib/routeHelper';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isDarkMode, toggleDarkMode } = useAppContext();
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isGroupActive = (group: NavigationGroup) => {
    return group.items.some(item => !item.hidden && isActive(item.path));
  };

  const renderNavigationGroupTrigger = (group: NavigationGroup) => {
    if (group.hidden) return null;
    
    const Icon = group.icon;
    const visibleItems = group.items.filter(item => !item.hidden);
    
    if (visibleItems.length === 0) return null;
    
    const columnCount = group.columnCount || 1;
    const menuWidth = group.menuWidth || "400px";

    const gridColsClass = {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4"
    }[columnCount];
    
    return (
      <NavigationMenuItem>
        <NavigationMenuTrigger className={cn(
          "text-gray-700 hover:text-blue-700 font-medium",
          isGroupActive(group) && "text-blue-800 bg-orange-100"
        )}>
          <Icon className="w-4 h-4 mr-2" />
          {t(group.title)}
        </NavigationMenuTrigger>
        <NavigationMenuContent>
          <ul 
            className={cn(
              "grid gap-3 p-4",
              gridColsClass
            )}
            style={{ width: menuWidth }}
          >
            {visibleItems.map((item, index) => (
              <li key={index}>
                <NavigationMenuLink asChild>
                  <Link to={getSeoRoute(item.path)} className={cn(
                    "flex items-center p-2 gap-2 rounded-md hover:bg-slate-100",
                    isActive(item.path) && "bg-orange-100 text-blue-800"
                  )}>
                    <item.icon className="w-5 h-5 text-blue-700" />
                    <div>
                      <div className="text-sm font-medium">{t(item.title)}</div>
                      {item.description && (
                        <p className="text-xs text-gray-500">{t(item.description)}</p>
                      )}
                    </div>
                  </Link>
                </NavigationMenuLink>
              </li>
            ))}
          </ul>
        </NavigationMenuContent>
      </NavigationMenuItem>
    );
  };

  const renderStandaloneItem = (item: NavigationItem) => {
    if (item.hidden) return null;
    
    const Icon = item.icon;
    
    return (
      <NavigationMenuItem>
        <NavigationMenuLink asChild>
          <Link 
            to={getSeoRoute(item.path)} 
            className={cn(
              "flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-gray-700 hover:text-blue-700 font-medium bg-white hover:bg-gray-50 transition-colors", 
              isActive(item.path) && "text-blue-800 bg-orange-100"
            )}
          >
            <Icon className="w-4 h-4 mr-2" />
            {t(item.title)}
          </Link>
        </NavigationMenuLink>
      </NavigationMenuItem>
    );
  };

  const renderMobileNavigationGroup = (group: NavigationGroup) => {
    if (group.hidden) return null;
    
    const Icon = group.icon;
    const visibleItems = group.items.filter(item => !item.hidden);
    
    if (visibleItems.length === 0) return null;
    
    return (
      <div className="flex flex-col space-y-1">
        <div className={cn(
          "text-gray-700 font-medium flex items-center gap-2",
          isGroupActive(group) && "text-blue-800"
        )}>
          <Icon className="w-4 h-4" />
          {t(group.title)}
        </div>
        <div className="ml-6 flex flex-col space-y-2 mt-1">
          {visibleItems.map((item, index) => (
            <Link 
              key={index}
              to={getSeoRoute(item.path)} 
              className={cn(
                "text-gray-600 hover:text-blue-700 text-sm flex items-center gap-2",
                isActive(item.path) && "text-blue-800 font-semibold bg-orange-100 px-2 py-1 rounded"
              )}
              onClick={() => setIsOpen(false)}
            >
              <item.icon className="w-4 h-4" />
              {t(item.title)}
            </Link>
          ))}
        </div>
      </div>
    );
  };

  const renderMobileStandaloneItem = (item: NavigationItem) => {
    if (item.hidden) return null;
    
    const Icon = item.icon;
    
    return (
      <Link 
        to={getSeoRoute(item.path)} 
        className={cn(
          "text-gray-700 hover:text-blue-700 font-medium flex items-center gap-2",
          isActive(item.path) && "text-blue-800 font-semibold bg-orange-100 px-2 py-1 rounded"
        )}
        onClick={() => setIsOpen(false)}
      >
        <Icon className="w-4 h-4" />
        {t(item.title)}
      </Link>
    );
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'} ${isDarkMode ? 'dark:bg-gray-900 dark:text-white' : ''}`}>
      <div className="container flex justify-between items-center">
        <Link to={getSeoRoute(ROUTES.HOME)} className="text-2xl font-heading font-bold text-blue-700">
          splusd<span className="text-orange-500">-solutions</span>
        </Link>

        <div className="hidden md:flex items-center space-x-6">
          <NavigationMenu>
            <NavigationMenuList>
              {renderNavigationGroupTrigger(NAVIGATION_CONFIG.consulting)}
              {renderNavigationGroupTrigger(NAVIGATION_CONFIG.development)}
              {renderNavigationGroupTrigger(NAVIGATION_CONFIG.products)}
              
              {NAVIGATION_CONFIG.standalone.map((item, index) => (
                renderStandaloneItem(item)
              ))}
            </NavigationMenuList>
          </NavigationMenu>
          
          <Button asChild variant="default" className="bg-orange-500 hover:bg-orange-600">
            <Link to="/about">Gespräch vereinbaren</Link>
          </Button>
        </div>

        <button className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-md py-4 md:hidden animate-fade-in">
            <div className="flex flex-col space-y-4 px-4">
              {renderMobileNavigationGroup(NAVIGATION_CONFIG.consulting)}
              {renderMobileNavigationGroup(NAVIGATION_CONFIG.development)}
              {renderMobileNavigationGroup(NAVIGATION_CONFIG.products)}
              
              {NAVIGATION_CONFIG.standalone.map((item, index) => (
                renderMobileStandaloneItem(item)
              ))}
              
              <Button asChild variant="default" className="bg-orange-500 hover:bg-orange-600">
                <Link to="/about" onClick={() => setIsOpen(false)}>Gespräch vereinbaren</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
